import { useState, useRef, useEffect, useCallback } from "react";
import { Paperclip, Download, FileText, Image as ImageIcon, Trash2, DownloadCloud } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  uploadComprovantes,
  getComprovantes,
  deleteComprovante,
  comprovanteDownloadUrl,
  comprovantesZipUrl,
} from "@/lib/ocorenApi";

const fmtBytes = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return "";
  }
};

export const ComprovantesDialog = ({ open, onOpenChange, nota, onChanged }) => {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const carregar = useCallback(async () => {
    if (!nota) return;
    setLoading(true);
    try {
      const r = await getComprovantes(nota.numero_nota, nota.cnpj_cliente);
      setComps(r.items || []);
    } catch {
      toast.error("Falha ao carregar comprovantes");
    } finally {
      setLoading(false);
    }
  }, [nota]);

  useEffect(() => {
    if (open) carregar();
  }, [open, carregar]);

  const onUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      await uploadComprovantes(nota.numero_nota, nota.cnpj_cliente, files);
      toast.success(`${files.length} comprovante(s) anexado(s)`);
      if (fileRef.current) fileRef.current.value = "";
      await carregar();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao anexar");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteComprovante(id);
      toast.success("Comprovante removido");
      await carregar();
      onChanged?.();
    } catch {
      toast.error("Falha ao remover");
    }
  };

  if (!nota) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="comprovante-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip size={18} /> Comprovantes — NF {nota.numero_nota}
          </DialogTitle>
          <DialogDescription>
            Anexe PDF, JPG ou PNG (vários de uma vez). Baixe um por um ou todos de uma vez.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 py-1">
          <input
            ref={fileRef}
            data-testid="comprovante-file-input"
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,application/pdf,image/*"
            onChange={onUpload}
            className="hidden"
          />
          <button
            data-testid="comprovante-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex-1 border-2 border-dashed border-[#E4E4E7] hover:border-[#09090B] rounded-md py-3 text-sm text-[#52525B] hover:text-[#09090B] flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            <Paperclip size={16} />
            {uploading ? "Enviando…" : "Anexar arquivo(s)"}
          </button>
          {comps.length > 0 && (
            <a
              data-testid="comprovante-download-all"
              href={comprovantesZipUrl(nota.numero_nota, nota.cnpj_cliente)}
              className="shrink-0 bg-[#09090B] text-white hover:bg-[#27272A] px-4 py-3 text-sm font-semibold rounded-md flex items-center gap-2"
            >
              <DownloadCloud size={15} />
              Baixar todos ({comps.length})
            </a>
          )}
        </div>

        <div className="mt-2 border border-[#E4E4E7] rounded-md overflow-hidden" data-testid="comprovante-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAFAFA] text-left text-[10px] uppercase tracking-[0.12em] text-[#52525B]">
                <th className="px-3 py-2 font-semibold">Arquivo</th>
                <th className="px-3 py-2 font-semibold">Tamanho</th>
                <th className="px-3 py-2 font-semibold">Enviado em</th>
                <th className="px-3 py-2 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-[#52525B]">Carregando…</td></tr>
              )}
              {!loading && comps.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-[#A1A1AA]">Nenhum comprovante anexado ainda.</td></tr>
              )}
              {comps.map((c, i) => {
                const isImg = (c.content_type || "").startsWith("image/");
                return (
                  <tr key={c.id} data-testid={`comprovante-item-${i}`} className="border-t border-[#E4E4E7]">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {isImg ? <ImageIcon size={15} className="text-[#3B82F6] shrink-0" /> : <FileText size={15} className="text-[#F43F5E] shrink-0" />}
                        <span className="text-[#09090B] truncate max-w-[320px]" title={c.original_filename}>{c.original_filename}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-[#52525B] font-mono-data">{fmtBytes(c.size)}</td>
                    <td className="px-3 py-2 text-xs text-[#52525B] font-mono-data whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <a
                          data-testid={`comprovante-download-${i}`}
                          href={comprovanteDownloadUrl(c.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 rounded-sm text-[#52525B] hover:text-[#09090B] hover:bg-[#FAFAFA]"
                          title="Baixar / visualizar"
                        >
                          <Download size={15} />
                        </a>
                        <button
                          data-testid={`comprovante-delete-${i}`}
                          onClick={() => onDelete(c.id)}
                          className="p-1.5 rounded-sm text-[#52525B] hover:text-[#F43F5E] hover:bg-[#FAFAFA]"
                          title="Remover"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ComprovantesDialog;
